import React, { useState, useEffect, useRef } from "react";
import { FaRupeeSign } from 'react-icons/fa';
import {
  FiClock,
  FiCalendar,
  FiBell,
  FiUsers,
  FiRefreshCw,
  FiDollarSign,
  FiBarChart2,
  FiFilter
} from "react-icons/fi";
import { apiCall } from "../services/authServieces";
import { Chart, LineController, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, BarController, BarElement } from 'chart.js';
import '../css/PriceWiseReport.css'

// Register Chart.js components
Chart.register(LineController, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, BarController, BarElement);

// Enhanced Bar Chart Component
const EnhancedBarChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="chart-no-data">No data available</div>;
  }

  return (
    <div className="enhanced-bar-chart">
      {data.map((item, index) => (
        <div key={index} className="enhanced-bar-item">
          <div className="enhanced-bar-info">
            <div className="enhanced-bar-label">
              <span className="color-dot" style={{ backgroundColor: item.color }}></span>
              {item.name}
            </div>
            <div className="enhanced-bar-value">{item.value}</div>
          </div>
          <div className="enhanced-bar-container">
            <div
              className="enhanced-bar-fill"
              style={{
                width: `${Math.min(item.value / Math.max(...data.map(d => d.value)) * 100, 100)}%`,
                backgroundColor: item.color
              }}
            ></div>
          </div>
        </div>
      ))}
    </div>
  );
};

function formatDateNew(dateStr) {
  return dateStr.split("-").reverse().join("-");
}



// Price Line Chart Component
const PriceLineChart = ({ data }) => {
  const chartRef = useRef(null);
  const [selectedMetric, setSelectedMetric] = useState('All metrics');

  useEffect(() => {
    if (!data || data.length === 0 || !chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');

    const formattedDates = data.map(item => item.date);

    const chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: formattedDates,
        datasets: getVisibleDatasets()
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return '₹' + value;
              }
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                return context.dataset.label + ': ₹' + context.raw;
              }
            }
          }
        }
      }
    });

    return () => chart.destroy();
  }, [data, selectedMetric]);

  const getVisibleDatasets = () => {
    const datasets = [];

    if (selectedMetric === 'All metrics' || selectedMetric === 'Marketing') {
      datasets.push({
        label: 'Marketing',
        data: data.map(item => item.marketingCharge || 0),
        backgroundColor: ' RGBA(16, 185, 129,0.2)',
        borderColor: '#10B981',
        borderWidth: 2,
        tension: 0.4,
        pointBackgroundColor: '#10B981'
      });
    }

    if (selectedMetric === 'All metrics' || selectedMetric === 'Utility') {
      datasets.push({
        label: 'Utility',
        data: data.map(item => item.utilityCharge || 0),
        backgroundColor: 'rgba(245, 158, 11, 0.2)',
        borderColor: '#F59E0B',
        borderWidth: 2,
        tension: 0.4,
        pointBackgroundColor: '#F59E0B'
      });
    }

    if (selectedMetric === 'All metrics' || selectedMetric === 'Total') {
      datasets.push({
        label: 'Total',
        data: data.map(item => item.totalPrice || 0),
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderColor: '#3B82F6',
        borderWidth: 2,
        tension: 0.4,
        pointBackgroundColor: '#3B82F6'
      });
    }

    return datasets;
  };

  const handleFilterChange = (e) => {
    setSelectedMetric(e.target.value);
  };

  if (!data || data.length === 0) {
    return <div className="flex justify-center items-center h-64 w-full bg-gray-50 rounded-lg">No data available</div>;
  }

  return (
    <div className="analytics-price-trends-container">
      <div className="analytics-price-trends-header">
        <h3 className="analytics-price-trends-title">Price Trends</h3>
        <select
          className="analytics-price-trends-select"
          onChange={handleFilterChange}
          value={selectedMetric}
        >
          <option value="All metrics">All metrics</option>
          <option value="Marketing">Marketing</option>
          <option value="Utility">Utility</option>
          <option value="Total">Total</option>
        </select>
      </div>

      <div className="analytics-price-trends-chart">
        <canvas ref={chartRef}></canvas>
      </div>

      <div className="analytics-price-trends-legend">
        <div className="analytics-legend-item">
          <span className="analytics-legend-dot analytics-legend-marketing"></span>
          <span className="analytics-legend-label">Marketing</span>
        </div>
        <div className="analytics-legend-item">
          <span className="analytics-legend-dot analytics-legend-utility"></span>
          <span className="analytics-legend-label">Utility</span>
        </div>
        <div className="analytics-legend-item">
          <span className="analytics-legend-dot analytics-legend-total"></span>
          <span className="analytics-legend-label">Total</span>
        </div>
      </div>
    </div>
  );
};

const getYesterdayDate = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toISOString().split('T')[0];
};

// Helper function to get next day of a given date
const getNextDay = (dateString) => {
  const date = new Date(dateString);
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
};


const AdminDashboard = () => {
  // State for loading and date range
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // const [dateRange, setDateRange] = useState({
  //   startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
  //   endDate: new Date().toISOString().split('T')[0]
  // });
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: getYesterdayDate() // Changed from today to yesterday
  });

  // State for message counts and pricing
  const [messageCounts, setMessageCounts] = useState({
    marketingCount: 0,
    utilityCount: 0,
    totalCount: 0
  });

  // State for pricing data
  const [pricingData, setPricingData] = useState({
    marketingPrice: 0,
    utilityPrice: 0,
    platformPrice: 0,
    marketingTotal: 0,
    utilityTotal: 0,
    totalAmount: 0,
    averageDaily: 0
  });

  // State for price history
  const [priceHistory, setPriceHistory] = useState([]);

  // State for chart data
  const [barChartData, setBarChartData] = useState([]);

  // Parse numeric value safely
  const parseNumeric = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()}/${date.getMonth() + 1}`;
  };

  // Function to fetch all dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    setRefreshing(true);

    try {
      const response = await apiCall({
        endpoint: `api/dealers/fetch_price_data`,
        method: "post",
        payload: {
          fromdate: dateRange.startDate,
          todate: dateRange.endDate
        }
      });
      if (response && response.success) {
        const priceData = response?.price_data;

        const marketingPrice = priceData.marketing_price || 0;
        const utilityPrice = priceData.utility_price || 0;
        const platformPrice = parseFloat(priceData.plt_price || 0);

        const messageData = response.data_marketing || [];

        let totalMarketingCount = 0;
        let totalUtilityCount = 0;

        messageData.forEach(day => {
          totalMarketingCount += parseNumeric(day.marketing_count || 0);
          totalUtilityCount += parseNumeric(day.utility_count || 0);
        });

        const totalCount = totalMarketingCount + totalUtilityCount;

        const marketingPriceWithPlatform = marketingPrice + platformPrice;
        const utilityPriceWithPlatform = utilityPrice + platformPrice;

        const marketingTotalWithPlatform = totalMarketingCount * marketingPriceWithPlatform;
        const utilityTotalWithPlatform = totalUtilityCount * utilityPriceWithPlatform;
        const totalAmount = marketingTotalWithPlatform + utilityTotalWithPlatform;

        const daysDiff = Math.max(1, Math.floor((new Date(dateRange.endDate) - new Date(dateRange.startDate)) / (1000 * 60 * 60 * 24)) + 1);
        const averageDaily = totalAmount / daysDiff;

        setMessageCounts({
          marketingCount: totalMarketingCount,
          utilityCount: totalUtilityCount,
          totalCount
        });

        setPricingData({
          marketingPrice: parseNumeric(marketingPriceWithPlatform.toFixed(2)),
          utilityPrice: parseNumeric(utilityPriceWithPlatform.toFixed(2)),
          platformPrice,
          marketingTotal: parseNumeric(marketingTotalWithPlatform.toFixed(2)),
          utilityTotal: parseNumeric(utilityTotalWithPlatform.toFixed(2)),
          totalAmount: parseNumeric(totalAmount.toFixed(2)),
          averageDaily: parseNumeric(averageDaily.toFixed(2))
        });

        setBarChartData([
          { name: "Marketing", value: totalMarketingCount, color: "#10B981" },
          { name: "Utility", value: totalUtilityCount, color: "#F59E0B" },
          { name: "Total", value: totalCount, color: "#4F46E5" }
        ]);

        const processedHistory = messageData.map(day => {
          const marketingCount = parseNumeric(day.marketing_count || 0);
          const utilityCount = parseNumeric(day.utility_count || 0);

          const marketingCharge = marketingCount * marketingPriceWithPlatform;
          const utilityCharge = utilityCount * utilityPriceWithPlatform;
          const totalPrice = marketingCharge + utilityCharge;

          return {
            date: formatDate(day.date),
            rawDate: new Date(day.date),
            marketingCount,
            utilityCount,
            marketingCharge: parseNumeric(marketingCharge.toFixed(2)),
            utilityCharge: parseNumeric(utilityCharge.toFixed(2)),
            totalPrice: parseNumeric(totalPrice.toFixed(2))
          };
        });

        processedHistory.sort((a, b) => a.rawDate - b.rawDate);
        setPriceHistory(processedHistory);
      } else {
        generateMockData();
      }
    } catch (error) {
      console.error("Error fetching dashboard data", error);
      generateMockData();
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Generate mock data for testing/fallback
  const generateMockData = () => {
    const marketingCount = Math.floor(Math.random() * 1000) + 500;
    const utilityCount = Math.floor(Math.random() * 500) + 200;

    const marketingPrice = 1.56;
    const utilityPrice = 0.56;

    const marketingTotal = marketingCount * marketingPrice;
    const utilityTotal = utilityCount * utilityPrice;
    const totalAmount = marketingTotal + utilityTotal;

    setMessageCounts({
      marketingCount,
      utilityCount,
      totalCount: marketingCount + utilityCount
    });

    setPricingData({
      marketingPrice,
      utilityPrice,
      platformPrice: 0.06,
      marketingTotal: parseNumeric(marketingTotal.toFixed(2)),
      utilityTotal: parseNumeric(utilityTotal.toFixed(2)),
      totalAmount: parseNumeric(totalAmount.toFixed(2)),
      averageDaily: parseNumeric((totalAmount / 30).toFixed(2))
    });

    setBarChartData([
      { name: "Marketing", value: marketingCount, color: "#10B981" },
      { name: "Utility", value: utilityCount, color: "#F59E0B" },
      { name: "Total", value: marketingCount + utilityCount, color: "#4F46E5" }
    ]);

    generateMockHistoricalData(marketingPrice, utilityPrice);
  };

  // Generate mock historical data for the chart
  const generateMockHistoricalData = (marketingPrice, utilityPrice) => {
    const mockHistory = [];
    const today = new Date(dateRange.endDate);
    const startDate = new Date(dateRange.startDate);
    const daysDiff = Math.floor((today - startDate) / (1000 * 60 * 60 * 24)) + 1;

    for (let i = 0; i < daysDiff; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);

      const marketingCount = Math.floor(Math.random() * 150) + 30;
      const utilityCount = Math.floor(Math.random() * 75) + 15;

      const marketingCharge = parseNumeric((marketingCount * marketingPrice).toFixed(2));
      const utilityCharge = parseNumeric((utilityCount * utilityPrice).toFixed(2));
      const totalPrice = parseNumeric((marketingCharge + utilityCharge).toFixed(2));

      mockHistory.push({
        date: formatDate(date),
        rawDate: date,
        marketingCount,
        utilityCount,
        marketingCharge,
        utilityCharge,
        totalPrice
      });
    }

    setPriceHistory(mockHistory);
  };

  // Handle date range change
  // const handleDateChange = (e) => {
  //   setDateRange({
  //     ...dateRange,
  //     [e.target.name]: e.target.value
  //   });
  // };

const handleDateChange = (e) => {
  const { name, value } = e.target;
  const yesterday = getYesterdayDate();
  if (value > yesterday) {
    return;
  }
  if (name === 'startDate') {
    const newEndDate = dateRange.endDate && dateRange.endDate >= value ? dateRange.endDate : '';
    setDateRange({
      startDate: value,
      endDate: newEndDate
    });
  } else {
    if (dateRange.startDate && value < dateRange.startDate) {
      return;
    }
    
    setDateRange({
      ...dateRange,
      [name]: value
    });
  }
};

  // Apply date filter
  const applyDateFilter = () => {
    fetchDashboardData();
  };

  // Fetch data on component load
  useEffect(() => {
    fetchDashboardData();
  }, []);

  return (
    <div className="admin-dashboard">
      <div className="Head_title2 analytics_header">
        <h1 className="dashboard-title">Analytics</h1>
        <div className="dashboard-actions">
          <div className="date-filter">
            <input
              type="date"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateChange}
              className="date-input"
              max={getYesterdayDate()}
            />
            <span className="date-separator">-</span>
            <input
              type="date"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateChange}
              className="date-input"
              min={dateRange.startDate}
              max={getYesterdayDate()}
              disabled={!dateRange.startDate}
            />
          </div>
          <button
            className="filter-button"
            onClick={applyDateFilter}
          >
            <FiFilter size={14} />
            Apply
          </button>
          <button
            className={`refresh-button ${refreshing ? 'refreshing' : ''}`}
            onClick={fetchDashboardData}
            disabled={refreshing}
          >
            <FiRefreshCw size={16} />
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>
      </div>

      {/* Loading indicator */}
      {loading && (
        <div className="loading-indicator">Loading dashboard data...</div>
      )}

      {/* Main Content Layout */}
      <div className="main-content">
        {/* Left Side: Message Summary */}
        <div className="left-panel">
          <div className="section-header">
            <h2 className="section-title">
              <FiUsers /> WhatsApp Summary
            </h2>
          </div>
          <div className="message-summary">
            <div className="message_summary_cards">
              <div className="stat-card message-card">
                <div className="stat-icon marketing-icon">
                  <FiUsers size={24} />
                </div>
                <div className="stat-info">
                  <h3 className="stat-value">{messageCounts.marketingCount}</h3>
                  <p className="stat-label">Marketing Messages</p>
                </div>
              </div>
              <div className="stat-card message-card">
                <div className="stat-icon utility-icon">
                  <FiUsers size={24} />
                </div>
                <div className="stat-info">
                  <h3 className="stat-value">{messageCounts.utilityCount}</h3>
                  <p className="stat-label">Utility Messages</p>
                </div>
              </div>
            </div>
            <div className="stat-card message-card">
              <div className="stat-icon total-icon">
                <FiUsers size={24} />
              </div>
              <div className="stat-info">
                <h3 className="stat-value">{messageCounts.totalCount}</h3>
                <p className="stat-label">Total Messages</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Message Activity Summary */}
        <div className="right-panel">
          <div className="section-header">
            <h2 className="section-title">
              <FiBarChart2 /> WhatsApp Activity Summary
            </h2>
          </div>
          <div className="chart-card">
            {barChartData && barChartData.length > 0 ? (
              <EnhancedBarChart data={barChartData} />
            ) : (
              <div className="chart-no-data">No WhatsApp activity data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Cost Breakdown Section */}
      <div className="section-header1">
        <h2 className="section-title">
          <FaRupeeSign /> Cost Breakdown
        </h2>
      </div>
      <div className="price-grid">
        <div className="stat-card price-card">
          <div className="stat-icon marketing-icon">
            <FaRupeeSign />
          </div>
          <div className="stat-info">
            <h3 className="stat-value">₹{pricingData.totalAmount}</h3>
            <p className="stat-label">Total Amount</p>
          </div>
        </div>
        <div className="stat-card price-card">
          <div className="stat-icon marketing-icon">
            <FaRupeeSign />
          </div>
          <div className="stat-info">
            <h3 className="stat-value">₹{pricingData.marketingTotal}</h3>
            <p className="stat-label">Marketing (@₹{Number(pricingData.marketingPrice).toFixed(2)}/msg)</p>
          </div>
        </div>
        <div className="stat-card price-card">
          <div className="stat-icon utility-icon">
            <FaRupeeSign />
          </div>
          <div className="stat-info">
            <h3 className="stat-value">₹{pricingData.utilityTotal}</h3>
            <p className="stat-label"> Utility (@₹{Number(pricingData.utilityPrice).toFixed(2)}/msg)</p>

          </div>
        </div>
        <div className="stat-card price-card">
          <div className="stat-icon">
            <FiCalendar size={24} />
          </div>
          <div className="stat-info">
            <h3 className="stat-value">₹{pricingData.averageDaily}</h3>
            <p className="stat-label">Daily Average</p>
          </div>
        </div>
      </div>

      {/* Total Amount Card */}
      {/* <div className="total-amount-card">
        <div className="total-amount-content">
          <div className="total-amount-label">Total Billing Amount</div>
          <div className="total-amount-value">₹{pricingData.totalAmount}</div>
        </div>
      </div>   */}

      {/* Price History Chart */}
      <div className="full-width-chart-card">
        <div className="chart-header">
          <h3 className="chart-title">
            <FiBarChart2 /> Daily Cost Breakdown ({formatDateNew(dateRange.startDate)} to {formatDateNew(dateRange.endDate)})
          </h3>
        </div>
        {priceHistory && priceHistory.length > 0 ? (
          <PriceLineChart data={priceHistory} />
        ) : (
          <div className="chart-no-data">No price history data available</div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;